"use client";

import { Link } from "@/i18n/navigation";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { components } from "@/openapi/generated";
import {
  formatShortDate,
  formatRelativeDate,
  formatImportance,
  truncateText,
  formatCategoryName,
} from "@/app/[locale]/release-notes/util/release-note-formatters";
import {
  CATEGORY_COLORS,
  CATEGORY_ICONS,
  CARD_TEXT_LIMITS,
} from "@/app/[locale]/release-notes/util/release-note-constants";
import { cn } from "@/lib/utils";
import { useTranslations } from "next-intl";

type ReleaseNoteListItem = components["schemas"]["ReleaseNoteListDto"];

interface ReleaseNoteCardProps {
  releaseNote: ReleaseNoteListItem;
  className?: string;
}

export function ReleaseNoteCard({ releaseNote, className }: ReleaseNoteCardProps) {
  const t = useTranslations("pages");

  const {
    slug,
    title,
    summary,
    category,
    importance,
    publishedAt,
    prId,
    prMergedAt,
  } = releaseNote;

  // Safety check only for optional publishedAt
  if (!publishedAt) {
    return null;
  }

  const displayDate = prMergedAt || publishedAt;
  const categoryColor = CATEGORY_COLORS[category];
  const categoryIcon = CATEGORY_ICONS[category];
  const categoryName = formatCategoryName(category);

  return (
    <Link href={`/release-notes/${slug}`} className="block group">
      <Card className={cn(
        "h-full transition-all duration-200 hover:shadow-lg hover:-translate-y-1 cursor-pointer",
        className
      )}>
        <CardHeader className="space-y-3">
          {/* Header with category and date */}
          <div className="flex justify-between items-start">
            <Badge className={cn("text-xs font-medium", categoryColor)}>
              <span className="mr-1">{categoryIcon}</span>
              {categoryName}
            </Badge>
            <time
              className="text-xs text-muted-foreground"
              dateTime={displayDate}
              title={formatShortDate(displayDate) ?? undefined}
            >
              {formatRelativeDate(displayDate)}
            </time>
          </div>

          {/* Title */}
          <h3 className="font-semibold text-lg leading-tight group-hover:text-primary transition-colors line-clamp-2">
            {title}
          </h3>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Summary */}
          <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3 min-h-[4.5rem]">
            {truncateText(summary, CARD_TEXT_LIMITS.summary)}
          </p>

          {/* Footer with importance and PR info */}
          <div className="flex justify-between items-center pt-2 border-t">
            <div className="flex items-center gap-2">
              {importance && (
                <div className="flex items-center gap-1">
                  <span
                    className="text-yellow-500 text-sm"
                    title={t("releaseNotes.importanceLabel", { importance })}
                  >
                    {formatImportance(importance)}
                  </span>
                </div>
              )}
            </div>

            {prId && (
              <div className="text-xs text-muted-foreground">
                {t("releaseNotes.pullRequestShort", { id: prId })}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
